import Typography from 'components/UI/Typography/Typography';

import { ROLES } from 'config/organization';

import { ReactComponent as ChartGrowIcon } from 'images/Icons/ChartGrow.svg';
import { ReactComponent as CheckMarkCircleIcon } from 'images/Icons/CheckMarkCircle.svg';
import { ReactComponent as CodeIcon } from 'images/Icons/Code.svg';
import { ReactComponent as DartTargetIcon } from 'images/Icons/DartTarget.svg';
import { ReactComponent as DocumentationIcon } from 'images/Icons/Documentation.svg';
import { ReactComponent as InfoChatIcon } from 'images/Icons/InfoChat.svg';
import { ReactComponent as PortfolioIcon } from 'images/Icons/Portfolio.svg';
import { ReactComponent as RocketIcon } from 'images/Icons/Rocket.svg';
import { ReactComponent as ShopIcon } from 'images/Icons/Shop.svg';
import { ReactComponent as TicketIcon } from 'images/Icons/Ticket.svg';
import { ReactComponent as WalletIcon } from 'images/Icons/Wallet.svg';
import { ReactComponent as WheelIcon } from 'images/Icons/Wheel.svg';

export const REWARD_SYSTEM_TYPES = {
  b2b: 'b2b',
  media: 'media',
};

export const REWARDS_PATH = 'rewards';

export const TABS = [
  {
    label: 'Progress',
    value: 'progress',
    Icon: DartTargetIcon,
  },
  {
    label: 'Rules',
    value: 'rules',
    Icon: InfoChatIcon,
  },
  {
    label: 'Terms and conditions',
    value: 'terms',
    Icon: DocumentationIcon,
  },
  {
    label: 'Rewards',
    value: REWARDS_PATH,
    Icon: WalletIcon,
  },
];

export const ANY_MONTH_OPTION = {
  label: 'Any month',
  value: 'any_month',
};

export const PERIOD_OPTIONS = [
  ANY_MONTH_OPTION,
  {
    label: '1 month',
    value: '1_month',
  },
  {
    label: '2 month',
    value: '2_months',
  },
  {
    label: '3 month',
    value: '3_months',
  },
];

export const BONUS_TASK_OPTION = {
  label: 'Bonus task',
  value: 'bonus_task',
};

export const KPI_KEYS = [
  'marketingKPI50',
  'marketingKPI100',
  'businessKPI50',
  'businessKPI75',
  'businessKPI100',
];

export const RULES = {
  howItWorks: {
    [ROLES.defaultUser]: [
      {
        title: 'Sign Up',
        description:
          'Register in the Rewards System and provide the necessary consents to participate — quick, easy, rewarding.',
        Icon: DartTargetIcon,
      },
      {
        title: 'Submit your marketing activities',
        description:
          'Complete a tailored set of marketing activities focused on promoting Evoplay games, tournaments, quest campaigns.',
        Icon: CheckMarkCircleIcon,
      },
      {
        title: 'Keep boosting your KPI',
        description:
          'Achieve your business targets by increasing your BETSUM by X% compared to the previous season (rolling 3-month period).',
        Icon: RocketIcon,
      },
      {
        title: 'Earn rewards to use it in shop',
        description:
          "As you complete activities and reach KPI milestones, you'll earn digital coins. Redeem them in our exclusive reward shop!",
        Icon: ShopIcon,
      },
    ],
    [ROLES.mediaAffiliate]: [
      {
        title: 'Sign Up',
        description:
          'Register in the Rewards System and provide the necessary consents to participate — quick, easy, rewarding.',
        Icon: DartTargetIcon,
      },
      {
        title: 'Submit media activities',
        description:
          'Complete six media activities each season to earn points - sixty points are needed per quarter for the drawing.',
        Icon: CheckMarkCircleIcon,
      },
      {
        title: 'Reach Your KPI',
        description:
          'Meet your quarterly KPI to join our media partners list and enter to win a $100 e-gift certificate. Five participants are selected each season.',
        Icon: RocketIcon,
      },
      {
        title: 'Join the raffle',
        description:
          'We add names of club members who completed KPIs to a Wheel of Fortune and select five winners. Results are shared via email.',
        Icon: TicketIcon,
      },
      {
        title: 'Earn rewards & shop',
        description:
          'The five lucky winners will each receive 100 digital coins, which can be redeemed in our exclusive reward shop.',
        Icon: ShopIcon,
      },
    ],
  },
  bonusOpportunity: [
    {
      title: 'Stay consistent and grow your $-KPI quarter by quarter',
      description: (
        <Typography color="grey">
          Reach at{' '}
          <Typography element="span" color="blue" weight="semibold">
            least 50% of your target KPI
          </Typography>{' '}
          each season, and you’ll qualify for our{' '}
          <Typography element="span" color="blue" weight="semibold">
            grand annual raffle
          </Typography>{' '}
          — with a chance to win an unforgettable trip to{' '}
          <Typography element="span" color="blue" weight="semibold">
            Maldives or Dubai
          </Typography>{' '}
          for 2–5 people!*
        </Typography>
      ),
      icon: '🎁',
    },
  ],
  bonusTasks: [
    {
      title: 'Activating balance increases',
      Icon: ChartGrowIcon,
    },
    {
      title: 'Launching or promoting free spins',
      Icon: WheelIcon,
    },
    {
      title: 'Adding Slotopia games to your portfolio',
      Icon: PortfolioIcon,
    },
    {
      title: 'Integrating Player Space',
      Icon: CodeIcon,
    },
  ],
};

export const SEASON_COLUMNS = [
  {
    id: 'activity',
    label: 'Activity name',
  },
  {
    id: 'reward',
    label: 'Reward',
  },
  {
    id: 'status',
    label: 'Status',
  },
  {
    id: 'accrued',
    label: 'Accrued',
  },
];

export const MEDIA_COMPLETED_TASKS_REQUIRED = 6;

export const MEDIA_BANNER_TYPES = {
  unReached: 'unReached',
  reached: 'reached',
  loss: 'loss',
  win: 'win',
};

export const MEDIA_BANNERS_CONFIG = {
  [MEDIA_BANNER_TYPES.unReached]: {
    title: (
      <Typography type="heading-sm" weight="bold" color="white" uppercase>
        Reach{' '}
        <Typography element="span" type="heading-sm" color="blue">
          {MEDIA_COMPLETED_TASKS_REQUIRED} goals per season
        </Typography>{' '}
        to qualify for the Grand Raffle!
      </Typography>
    ),
  },
  [MEDIA_BANNER_TYPES.reached]: {
    title: (
      <Typography type="heading-sm" weight="bold" color="white" uppercase>
        You’ve completed{' '}
        <Typography element="span" type="heading-sm" color="purple">
          all the tasks
        </Typography>{' '}
        and are now entered into{' '}
        <Typography element="span" type="heading-sm" color="purple">
          the Grand Raffle!
        </Typography>
      </Typography>
    ),
  },
  [MEDIA_BANNER_TYPES.loss]: {
    title: (
      <Typography type="heading-sm" weight="bold" color="white" uppercase>
        <Typography element="span" type="heading-sm" color="pink">
          You didn’t
        </Typography>{' '}
        win this time
      </Typography>
    ),
    description: 'Try again next time - luck might be on your side!',
  },
  [MEDIA_BANNER_TYPES.win]: {
    title: (
      <Typography type="heading-sm" weight="bold" color="white" uppercase>
        <Typography element="span" type="heading-sm" color="green">
          You won
        </Typography>{' '}
        the raffle!
      </Typography>
    ),
    description:
      'Your manager will contact you with the details on how to claim your prize.',
  },
};
